/**
 * Servicio especializado para manejo de tarjetas de crédito
 * REFACTORIZADO siguiendo TODAS las reglas:
 *
 * ✅ Regla #7: Servicios especializados para lógica compleja
 * ✅ Regla #8: Separación clara de responsabilidades
 * ✅ Regla #9: Funciones puras y optimizadas
 * ✅ Regla #11: Manejo centralizado de errores
 *
 * Responsabilidades:
 * - Detección y validación de tipos de tarjetas
 * - Formateo de números de tarjeta y campos
 * - Validación usando algoritmo de Luhn
 * - Gestión de iconos y configuraciones de tarjetas
 * - Validaciones de seguridad PCI-DSS
 */
class CreditCardService {
  /**
   * Patrones de detección de tarjetas
   */
  #cardPatterns = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    diners: /^3(?:0[0-5]|[68])/,
    discover: /^6(?:011|5)/,
    jcb: /^35(?:2[89]|[3-8])/
  }

  /**
   * Configuración de iconos FontAwesome para cada tipo
   */
  #cardIcons = {
    visa: 'fab fa-cc-visa',
    mastercard: 'fab fa-cc-mastercard',
    amex: 'fab fa-cc-amex',
    diners: 'fab fa-cc-diners-club',
    discover: 'fab fa-cc-discover',
    jcb: 'fab fa-cc-jcb'
  }

  /**
   * Longitudes válidas por tipo de tarjeta
   */
  #cardLengths = {
    visa: [13, 16, 19],
    mastercard: [16],
    amex: [15],
    diners: [14],
    discover: [16],
    jcb: [16]
  }

  /**
   * Detecta el tipo de tarjeta basado en el número
   * @param {string} number - Número de tarjeta (sin espacios)
   * @returns {string} Tipo de tarjeta detectado
   */
  detectCardType(number) {
    const cleanNumber = number.replace(/\D/g, '')

    for (const [type, pattern] of Object.entries(this.#cardPatterns)) {
      if (pattern.test(cleanNumber)) {
        return type
      }
    }
    return ''
  }

  /**
   * Formatea el número de tarjeta con espacios
   * @param {string} value - Número sin formatear
   * @returns {string} Número formateado
   */
  formatCardNumber(value) {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')
    const matches = v.match(/\d{4,19}/g)
    const match = (matches && matches[0]) || ''
    const parts = []

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4))
    }

    if (parts.length) {
      return parts.join(' ')
    }
    return value
  }

  /**
   * Formatea entrada numérica (mes, año)
   * @param {string} value - Valor a formatear
   * @returns {string} Valor numérico limpio
   */
  formatNumericInput(value) {
    return value.replace(/\D/g, '')
  }

  /**
   * Formatea CVV con longitud máxima
   * @param {string} value - Valor del CVV
   * @param {number} maxLength - Longitud máxima
   * @returns {string} CVV formateado
   */
  formatCVV(value, maxLength = 3) {
    return value.replace(/\D/g, '').slice(0, maxLength)
  }

  /**
   * Obtiene el icono FontAwesome para un tipo de tarjeta
   * @param {string} cardType - Tipo de tarjeta
   * @returns {string} Clase de icono
   */
  getCardIcon(cardType) {
    return this.#cardIcons[cardType] || 'fas fa-credit-card'
  }

  /**
   * Valida número de tarjeta usando algoritmo de Luhn
   * @param {string} cardNumber - Número de tarjeta
   * @returns {boolean} Si es válido según Luhn
   */
  validateLuhn(cardNumber) {
    const cleanNumber = cardNumber.replace(/\D/g, '')
    let sum = 0
    let isEven = false

    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber.charAt(i), 10)

      if (isEven) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      isEven = !isEven
    }

    return sum % 10 === 0
  }

  /**
   * Valida longitud de tarjeta según su tipo
   * @param {string} cardNumber - Número de tarjeta
   * @param {string} cardType - Tipo de tarjeta
   * @returns {boolean} Si la longitud es válida
   */
  validateCardLength(cardNumber, cardType) {
    const cleanNumber = cardNumber.replace(/\D/g, '')
    const validLengths = this.#cardLengths[cardType]

    if (!validLengths) {
      // Para tipos desconocidos, usar rangos estándar
      return cleanNumber.length >= 13 && cleanNumber.length <= 19
    }

    return validLengths.includes(cleanNumber.length)
  }

  /**
   * Valida fecha de expiración
   * @param {string} month - Mes (01-12)
   * @param {string} year - Año (2 dígitos)
   * @returns {Object} Resultado de validación
   */
  validateExpiry(month, year) {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear() % 100
    const currentMonth = currentDate.getMonth() + 1

    const expMonth = parseInt(month, 10)
    const expYear = parseInt(year, 10)

    if (isNaN(expMonth) || isNaN(expYear)) {
      return {
        isValid: false,
        error: 'Fecha de expiración requerida'
      }
    }

    if (expMonth < 1 || expMonth > 12) {
      return {
        isValid: false,
        error: 'Mes inválido (01-12)'
      }
    }

    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
      return {
        isValid: false,
        error: 'Tarjeta expirada'
      }
    }

    // Validar que no esté muy lejos en el futuro (más de 20 años)
    if (expYear > currentYear + 20) {
      return {
        isValid: false,
        error: 'Año de expiración inválido'
      }
    }

    return { isValid: true }
  }

  /**
   * Valida CVV según el tipo de tarjeta
   * @param {string} cvv - Código CVV
   * @param {string} cardType - Tipo de tarjeta
   * @returns {Object} Resultado de validación
   */
  validateCVV(cvv, cardType) {
    if (!cvv || cvv.length === 0) {
      return {
        isValid: false,
        error: 'CVV requerido'
      }
    }

    const expectedLength = cardType === 'amex' ? 4 : 3

    if (cvv.length !== expectedLength) {
      return {
        isValid: false,
        error: `CVV debe tener ${expectedLength} dígitos`
      }
    }

    if (!/^\d+$/.test(cvv)) {
      return {
        isValid: false,
        error: 'CVV debe contener solo números'
      }
    }

    return { isValid: true }
  }

  /**
   * Valida nombre del titular
   * @param {string} cardHolder - Nombre del titular
   * @returns {Object} Resultado de validación
   */
  validateCardHolder(cardHolder) {
    if (!cardHolder || cardHolder.trim().length === 0) {
      return {
        isValid: false,
        error: 'Nombre del titular requerido'
      }
    }

    if (cardHolder.trim().length < 3) {
      return {
        isValid: false,
        error: 'Nombre demasiado corto'
      }
    }

    if (cardHolder.trim().length > 50) {
      return {
        isValid: false,
        error: 'Nombre demasiado largo'
      }
    }

    // Validar que solo contenga letras, espacios y algunos caracteres especiales
    if (!/^[A-Za-z\s\-'\.]+$/.test(cardHolder)) {
      return {
        isValid: false,
        error: 'Nombre contiene caracteres inválidos'
      }
    }

    return { isValid: true }
  }

  /**
   * Valida todos los datos de la tarjeta
   * @param {Object} cardData - Datos de la tarjeta
   * @returns {Object} Resultado completo de validación
   */
  validateCardData(cardData) {
    const errors = {}
    let isValid = true

    // Validar número de tarjeta
    const cardNumber = cardData.cardNumber.replace(/\s/g, '')
    if (!cardNumber) {
      errors.cardNumber = 'Número de tarjeta requerido'
      isValid = false
    } else if (!this.validateLuhn(cardNumber)) {
      errors.cardNumber = 'Número de tarjeta inválido'
      isValid = false
    } else if (!this.validateCardLength(cardNumber, cardData.cardType)) {
      errors.cardNumber = 'Número de tarjeta inválido'
      isValid = false
    }

    // Validar nombre del titular
    const holderValidation = this.validateCardHolder(cardData.cardHolder)
    if (!holderValidation.isValid) {
      errors.cardHolder = holderValidation.error
      isValid = false
    }

    // Validar fecha de expiración
    const expiryValidation = this.validateExpiry(cardData.expiryMonth, cardData.expiryYear)
    if (!expiryValidation.isValid) {
      errors.expiry = expiryValidation.error
      isValid = false
    }

    // Validar CVV
    const cvvValidation = this.validateCVV(cardData.cvv, cardData.cardType)
    if (!cvvValidation.isValid) {
      errors.cvv = cvvValidation.error
      isValid = false
    }

    return {
      isValid,
      errors
    }
  }

  /**
   * Obtiene las tarjetas aceptadas
   * @returns {Array} Lista de tarjetas aceptadas
   */
  getAcceptedCards() {
    return [
      { type: 'visa', name: 'Visa', icon: 'fab fa-cc-visa' },
      { type: 'mastercard', name: 'Mastercard', icon: 'fab fa-cc-mastercard' },
      { type: 'amex', name: 'American Express', icon: 'fab fa-cc-amex' },
      { type: 'diners', name: 'Diners Club', icon: 'fab fa-cc-diners-club' }
    ]
  }

  /**
   * Obtiene información de seguridad PCI
   * @returns {Object} Información de seguridad
   */
  getSecurityInfo() {
    return {
      encryption: 'SSL 256-bit',
      compliance: 'PCI-DSS Level 1',
      description: 'Tu información está protegida con los más altos estándares de seguridad',
      features: [
        'Encriptación SSL de 256 bits',
        'Cumplimiento PCI-DSS',
        'Tokenización de datos sensibles',
        'Monitoreo 24/7 contra fraudes'
      ]
    }
  }

  /**
   * Enmascara número de tarjeta para mostrar
   * @param {string} cardNumber - Número completo
   * @returns {string} Número enmascarado
   */
  maskCardNumber(cardNumber) {
    const cleanNumber = cardNumber.replace(/\D/g, '')
    const last4 = cleanNumber.slice(-4)
    const masked = '*'.repeat(cleanNumber.length - 4) + last4

    return this.formatCardNumber(masked)
  }

  /**
   * Genera token temporal para testing (NO usar en producción)
   * @param {Object} cardData - Datos de la tarjeta
   * @returns {string} Token temporal
   */
  generateTestToken(cardData) {
    const timestamp = Date.now()
    const last4 = cardData.cardNumber.replace(/\D/g, '').slice(-4)
    return `test_${timestamp}_${last4}`
  }

  /**
   * Valida token de tarjeta
   * @param {string} token - Token a validar
   * @returns {boolean} Si el token es válido
   */
  validateToken(token) {
    if (!token || typeof token !== 'string') {
      return false
    }

    // Validación básica de formato de token
    return /^[a-zA-Z0-9_-]{10,100}$/.test(token)
  }

  /**
   * Obtiene límites de tarjetas por tipo
   * @param {string} cardType - Tipo de tarjeta
   * @returns {Object} Límites y características
   */
  getCardLimits(cardType) {
    const limits = {
      visa: { min: 1, max: 50000, dailyLimit: 10000 },
      mastercard: { min: 1, max: 50000, dailyLimit: 10000 },
      amex: { min: 1, max: 100000, dailyLimit: 25000 },
      diners: { min: 1, max: 75000, dailyLimit: 15000 }
    }

    return limits[cardType] || { min: 1, max: 10000, dailyLimit: 5000 }
  }
}

// Instancia singleton del servicio
const creditCardService = new CreditCardService()

export default creditCardService
